import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sliders, RefreshCw, Eye, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LogoPlacementEditor — 4 sliders that map directly to the
 * `invoice_settings` JSON on a company record:
 *
 *   • logo_x_mm           — horizontal offset from the LEFT page edge
 *   • logo_y_offset_mm    — vertical offset from the TOP page edge
 *   • logo_height_mm      — rendered logo height in millimetres
 *   • logo_max_width_mm   — cap on the rendered width (keeps aspect ratio)
 *
 * The right side renders a LIVE preview by calling the new
 * `GET /api/companies/{id}/invoice-preview` endpoint (re-fetched
 * each time the admin lets a slider settle).
 *
 * NOTE: the previous `previewUrl` was kept in `useState` AND listed
 * as a dep of `reloadPreview` — which triggered an infinite refetch
 * loop (every fetch set a new URL → new callback identity → effect
 * re-ran → new fetch …). The current implementation stores the URL
 * in a ref AND in state, but the ref is what the cleanup reads,
 * and `reloadPreview` no longer depends on the URL, killing the loop
 * dead.
 */
const DEFAULTS = {
  logo_x_mm:           18,
  logo_y_offset_mm:    32,
  logo_height_mm:      18,
  logo_max_width_mm:   60,
};

const RANGES = {
  logo_x_mm:           { min: 0, max: 180, step: 1, label: 'X (from left, mm)' },
  logo_y_offset_mm:    { min: 6, max: 60,  step: 1, label: 'Y offset (from top, mm)' },
  logo_height_mm:      { min: 6, max: 40,  step: 0.5, label: 'Height (mm)' },
  logo_max_width_mm:   { min: 10, max: 120, step: 1, label: 'Max width (mm)' },
};

export default function LogoPlacementEditor({ companyId, token, value, onChange }) {
  const cur = { ...DEFAULTS, ...(value || {}) };
  const [previewBust, setPreviewBust] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const blobUrlRef = useRef('');

  // Re-fetch the preview when `previewBust` increments. We do NOT
  // include `previewUrl` in the deps anymore — that was the source
  // of the flicker (state change → new callback → effect re-runs →
  // new fetch → state change → …).
  const reloadPreview = useCallback(async () => {
    if (!companyId) return;
    setLoadingPreview(true);
    try {
      const r = await fetch(`${API_URL}/api/companies/${companyId}/invoice-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      // Force the correct MIME so some browsers can render the PDF
      // inside an <iframe> (Edge sometimes refuses application/octet-stream).
      const pdfBlob = blob.type === 'application/pdf'
        ? blob
        : new Blob([blob], { type: 'application/pdf' });
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(pdfBlob);
      blobUrlRef.current = url;
      setPreviewUrl(url);
    } catch (_) { /* keep previous preview on transient failure */ }
    finally { setLoadingPreview(false); }
  }, [companyId, token]);

  useEffect(() => { reloadPreview(); }, [previewBust, reloadPreview]);

  // Final blob cleanup on unmount (component leaves the DOM).
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const set = (k, v) => onChange?.({ ...cur, [k]: v });

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-testid="logo-placement-editor">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sliders className="w-4 h-4 text-cyan-700" />
          <h4 className="text-sm font-bold text-slate-800">Logo placement on invoice</h4>
        </div>
        <button
          type="button"
          onClick={() => onChange?.({ ...DEFAULTS })}
          data-testid="logo-placement-reset"
          className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Reset defaults
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {Object.entries(RANGES).map(([k, r]) => (
          <div key={k}>
            <div className="flex items-center justify-between text-[11px] font-semibold text-slate-600 mb-1">
              <span>{r.label}</span>
              <span className="font-mono tabular-nums text-slate-800">{cur[k]} mm</span>
            </div>
            <input
              type="range"
              min={r.min} max={r.max} step={r.step}
              value={cur[k]}
              onChange={(e) => set(k, Number(e.target.value))}
              onMouseUp={() => setPreviewBust(x => x + 1)}
              onTouchEnd={() => setPreviewBust(x => x + 1)}
              onKeyUp={() => setPreviewBust(x => x + 1)}
              data-testid={`logo-${k}`}
              className="w-full accent-cyan-600"
            />
          </div>
        ))}
      </div>

      <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] uppercase font-bold text-slate-600 tracking-wider flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Live preview
          </span>
          <div className="flex items-center gap-2">
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 flex items-center gap-1"
              >
                <Eye className="w-3 h-3" /> Open PDF
              </a>
            )}
            <button
              type="button"
              onClick={() => setPreviewBust(x => x + 1)}
              disabled={loadingPreview}
              data-testid="logo-placement-refresh-preview"
              className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 flex items-center gap-1 disabled:opacity-50"
            >
              {loadingPreview ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
          </div>
        </div>
        <p className="text-[11px] text-amber-700 mb-2">
          Save the company first to apply the sliders below to the preview — the preview reads the SAVED state.
        </p>
        {previewUrl ? (
          <iframe
            title="Logo placement preview"
            src={previewUrl}
            className="w-full h-80 rounded-lg border border-slate-200 bg-white"
            data-testid="logo-placement-preview-iframe"
          />
        ) : (
          <div className="w-full h-80 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-slate-500 text-sm">
            {loadingPreview ? 'Loading preview…' : 'Click "Refresh" to render the current saved state.'}
          </div>
        )}
      </div>
    </div>
  );
}
